import { IDependentValues, IFormatterProps } from '../../@types/workspaceTypes';
import { useSelector } from 'react-redux';
import React, { MouseEventHandler, useContext, useMemo } from 'react';
import RowMoveCell from '../react-data-grid-addons/src/draggable/RowMoveCell';
import { mergeClasses, Popover, PopoverSurface, PopoverTrigger } from '@fluentui/react-components';
import { useQuery } from '@apollo/client';
import StockTotalQuery from '../../graphql/queries/product/StockTotalQuery.graphql';
import { BasicHeader } from '../../actions/BasicHeader';
import Row from '../connectedForm/Row';
import Fieldset from '../connectedForm/Fieldset';
import {
  IFullUserDetails,
  IPurchaseOrderLine,
  ISalesOrderLine,
  IStockTotalQueryResult,
  IStockTotalQueryVariables,
  Maybe,
  ProductClass,
  SalesOrderLineStatus,
} from '../../graphql/schema/p4types';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import { round, RoundingModes } from '../../utils/NumberUtils';
import { DragTitle, STATUS_CELL_BASIC_HEADER_STYLES, StatusCircle, useStatusCellStyles } from './util';

/**
 * List of statuses considered "Ready" (Blue Status Flag)
 */
const READY_STATUSES = [
  SalesOrderLineStatus.Allocated,
  SalesOrderLineStatus.InProduction,
  SalesOrderLineStatus.Assigned,
  SalesOrderLineStatus.AssignedShortage,
  SalesOrderLineStatus.AssignedAssembly,
  SalesOrderLineStatus.AssignedAssemblyShortage,
];

/**
 * List of statuses considered "Successful" (Green Status Flag)
 */
const SUCCESS_STATUSES = [SalesOrderLineStatus.Dispatched, SalesOrderLineStatus.Invoiced];

/**
 * Get the standard unit quantity variance of the provided salesOrderLine since it was last persisted (Or just the new standard unit quantity if it's a new line).
 */
export const useVarianceQuantity = ({ orderLine }: { orderLine?: ISalesOrderLine | IPurchaseOrderLine }) => {
  const { baseValues } = useContext(ConnectedFormContext);
  const baseOrderLines = useMemo(() => baseValues?.orderLines ?? [], [baseValues?.orderLines]);
  return useMemo(() => {
    if (!orderLine) {
      return 0;
    }

    const { quantity, activeSellingUnit, id } = orderLine;

    // In order to reach a short quantity that takes alternate selling units into account, need to calculate
    // standard unit quantity based on quantity (standard unit quantity on the line is not yet up-to-date)
    let newStandardUnitQuantity = parseFloat(quantity);
    if (activeSellingUnit) {
      if (activeSellingUnit.factorDirection === 'FROM_BASE_UNIT') {
        newStandardUnitQuantity = parseFloat(quantity) * activeSellingUnit.quantityFactor;
      } else {
        newStandardUnitQuantity = parseFloat(quantity) / activeSellingUnit.quantityFactor;
      }
    }

    const oldOrderLine = id && baseOrderLines.find((line: ISalesOrderLine) => line.id === id);

    if (oldOrderLine) {
      return round(
        newStandardUnitQuantity - oldOrderLine.standardUnitQuantity,
        RoundingModes.NEAREST,
        (oldOrderLine as ISalesOrderLine).quantityPrecision ?? 2
      );
    }
    return newStandardUnitQuantity;
  }, [baseOrderLines, orderLine]);
};

const SalesOrderStatusDisplay = ({
  dependentValues,
  localVarianceQuantity,
}: {
  dependentValues: IDependentValues<ISalesOrderLine>;
  localVarianceQuantity: number;
}) => {
  const styles = useStatusCellStyles();

  const title = useMemo(() => {
    const description = dependentValues.rowData.description;
    if (description) {
      return `Status Display - ${description}`;
    }
    return 'Status Display';
  }, [dependentValues.rowData.description]);

  const { data } = useQuery<IStockTotalQueryResult, IStockTotalQueryVariables>(StockTotalQuery, {
    variables: {
      productId: dependentValues.rowData.product?.id,
      warehouseId: dependentValues.rowData.warehouse?.id,
    },
    skip: !dependentValues.rowData.product?.id || !dependentValues.rowData.warehouse?.id,
  });

  const outstandingQuantity = useMemo(() => {
    const isFreeType = dependentValues?.rowData?.product?.productClass === ProductClass.FreeType;
    return isFreeType ? 0 : (dependentValues.rowData.outstanding ?? 0) + localVarianceQuantity;
  }, [dependentValues.rowData.outstanding, localVarianceQuantity, dependentValues?.rowData?.product?.productClass]);

  const insufficientStockQuantity = useMemo(() => {
    const freeStock = data?.data?.freeStock ?? 0;
    const makeable = data?.data?.makeable ?? 0;
    return Math.max(outstandingQuantity - freeStock - makeable, 0);
  }, [data?.data?.freeStock, data?.data?.makeable, outstandingQuantity]);

  const outstandingInStockQuantity = useMemo(() => {
    return outstandingQuantity - insufficientStockQuantity;
  }, [insufficientStockQuantity, outstandingQuantity]);

  const awaitingText = `Awaiting ${
    dependentValues.rowData.product?.productClass === ProductClass.Kit ? 'Production' : 'Allocation'
  }`;

  return (
    <>
      <BasicHeader secondaryTitle={<DragTitle />} styles={STATUS_CELL_BASIC_HEADER_STYLES}>
        {title}
      </BasicHeader>
      <div style={{ padding: '1em', minWidth: '650px' }}>
        <Row>
          <Fieldset cols={1 / 3}>
            <StatusCircle
              message={`${awaitingText} (Insufficient Stock): ${insufficientStockQuantity ?? 0}`}
              iconClassName={styles.dangerCircle}
            />
          </Fieldset>
          <Fieldset cols={1 / 3}>
            <StatusCircle
              message={`${awaitingText} (In Stock): ${outstandingInStockQuantity ?? 0}`}
              iconClassName={styles.warningCircle}
            />
            <StatusCircle
              message={`Reserved (Kits): ${dependentValues.rowData.reserved ?? 0}`}
              iconClassName={styles.warningCircle}
            />
          </Fieldset>
          <Fieldset cols={1 / 3}>
            <StatusCircle
              message={`Assigned: ${dependentValues.rowData.assigned ?? 0}`}
              iconClassName={styles.readyCircle}
            />
            <StatusCircle
              message={`Allocated: ${dependentValues.rowData.allocated ?? 0}`}
              iconClassName={styles.readyCircle}
            />
            <StatusCircle
              message={`In Production: ${dependentValues.rowData.inProduction ?? 0}`}
              iconClassName={styles.readyCircle}
            />
          </Fieldset>
          <Fieldset cols={1 / 3}>
            <StatusCircle
              message={`Dispatched: ${dependentValues.rowData.dispatched ?? 0}`}
              iconClassName={styles.successCircle}
            />
            <StatusCircle
              message={`Invoiced: ${dependentValues.rowData.invoiced ?? 0}`}
              iconClassName={styles.successCircle}
            />
          </Fieldset>
        </Row>
      </div>
    </>
  );
};
export const SalesOrderStatusCellFormatter = (props: IFormatterProps<ISalesOrderLine>) => {
  const fullUserDetails = useSelector((store: any) => store.app.fullUserDetails as Maybe<IFullUserDetails>);
  const { dependentValues, rowIdx, idx, column } = props;
  const styles = useStatusCellStyles();
  const { values } = useContext(ConnectedFormContext);

  const localVarianceQuantity = useVarianceQuantity({ orderLine: dependentValues.rowData });

  const onClick: MouseEventHandler<any> = (e) => {
    const meta = dependentValues.cellMetaData;
    if (meta?.onCellClick && typeof meta.onCellClick === 'function') {
      meta.onCellClick({ rowIdx: rowIdx, idx: idx }, e);
    }
  };

  //If status display is disabled, or no product is selected yet, show the regular RowMoveCell component with standard styling.
  if (
    !fullUserDetails?.companyUser?.additionalStatusDisplays ||
    !dependentValues.rowData.product ||
    dependentValues.rowData.archived ||
    values?.archived
  ) {
    return (
      <div
        className={'react-grid-Cell'}
        style={{ width: column.width, height: '36px', position: 'absolute' }}
        onClick={onClick}
      >
        <div className="react-grid-Cell__value" style={{ position: 'relative' }}>
          <RowMoveCell {...props} showTooltip={true} />
        </div>
      </div>
    );
  }

  const statuses: Array<Maybe<SalesOrderLineStatus>> = dependentValues.rowData?.salesOrderLineStatus ?? [];

  const outstanding = dependentValues.rowData.outstanding ?? 0;
  const isFreeType = dependentValues?.rowData?.product?.productClass === ProductClass.FreeType;

  const hasOutstandingQuantity = -localVarianceQuantity < outstanding;

  const hasReservations = statuses.some((status) => status === SalesOrderLineStatus.Reserved);
  // If all statuses are reserved or above, consider this to be fully reserved.
  const fullyReserved =
    hasReservations &&
    statuses.every(
      (status) =>
        READY_STATUSES.includes(status!) ||
        SUCCESS_STATUSES.includes(status!) ||
        status === SalesOrderLineStatus.Reserved
    );
  const partiallyReserved = hasReservations && !fullyReserved;

  let cellClassName: string | undefined;
  if (!isFreeType && (hasOutstandingQuantity || partiallyReserved)) {
    cellClassName = styles.cellDanger;
  } else if (fullyReserved) {
    cellClassName = styles.cellWarning;
  } else if (statuses.some((status) => READY_STATUSES.includes(status!))) {
    cellClassName = styles.cellReady;
  } else if (statuses.some((status) => SUCCESS_STATUSES.includes(status!))) {
    cellClassName = styles.cellSuccess;
  }

  return (
    <Popover openOnHover={true} positioning={'after-bottom'} mouseLeaveDelay={0}>
      <PopoverTrigger>
        <div
          className={mergeClasses(styles.cell, cellClassName)}
          style={{ width: column.width, height: 36 }}
          onClick={onClick}
        >
          <div className="react-grid-Cell__value" style={{ position: 'relative' }}>
            <RowMoveCell {...props} showTooltip={false} />
          </div>
        </div>
      </PopoverTrigger>
      <PopoverSurface className={styles.surface}>
        <SalesOrderStatusDisplay dependentValues={dependentValues} localVarianceQuantity={localVarianceQuantity} />
      </PopoverSurface>
    </Popover>
  );
};
